import { create } from 'zustand'
import { galleryFilename } from '@/lib/galleryFilename'
import { LocalFolderConnector } from '@/lib/storage/LocalFolderConnector'
import { GoogleDriveConnector, GisTokenProvider } from '@/lib/storage/GoogleDriveConnector'
import { getDevicePassphrase } from '@/lib/keyStore'
import { useKeysStore } from '@/stores/keys'

// Lazy singletons — created on first use so browser globals are not accessed at import time.
// Exported for testing only (allows vi.spyOn on the instances).
let _localConnectorInstance: LocalFolderConnector | null = null
let _googleConnectorInstance: GoogleDriveConnector | null = null

// Client ID resolved during init(): key store value takes priority, env var is fallback.
let _driveClientId = ''

export function _getLocalConnector(): LocalFolderConnector {
  return (_localConnectorInstance ??= new LocalFolderConnector())
}

export function _getGoogleConnector(): GoogleDriveConnector {
  return (_googleConnectorInstance ??= new GoogleDriveConnector({
    tokenProvider: new GisTokenProvider(() => _driveClientId),
    passphrase: typeof localStorage !== 'undefined' ? getDevicePassphrase() : 'ssr-placeholder',
  }))
}

interface LocalFolderState {
  configured: boolean
  folderName: string | null
  permissionLost: boolean
}

interface GoogleDriveState {
  configured: boolean
  accountEmail: string | null
  googleClientId: string | null
}

interface StorageState {
  localFolder: LocalFolderState
  googleDrive: GoogleDriveState
}

interface StorageActions {
  init(): Promise<void>
  connectLocalFolder(): Promise<void>
  disconnectLocalFolder(): Promise<void>
  connectGoogleDrive(): Promise<void>
  disconnectGoogleDrive(): Promise<void>
  saveGoogleClientId(clientId: string): Promise<void>
  removeGoogleClientId(): Promise<void>
  saveToAll(imageUrl: string, model: string, filename?: string): Promise<void>
}

export const useStorageStore = create<StorageState & StorageActions>()((set) => ({
  localFolder: { configured: false, folderName: null, permissionLost: false },
  googleDrive: { configured: false, accountEmail: null, googleClientId: null },

  async init() {
    // Resolve client ID: key store takes priority over env var
    const storedClientId = await useKeysStore.getState().getKey('google-drive-client')
    _driveClientId = storedClientId ?? import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''

    const local = _getLocalConnector()
    const google = _getGoogleConnector()
    await Promise.all([local.init(), google.init()])
    set({
      localFolder: {
        configured: local.isConfigured(),
        folderName: local.folderName,
        permissionLost: local.permissionLost,
      },
      googleDrive: {
        configured: google.isConfigured(),
        accountEmail: google.accountEmail,
        googleClientId: _driveClientId || null,
      },
    })
  },

  async connectLocalFolder() {
    const local = _getLocalConnector()
    await local.pick()
    set({
      localFolder: {
        configured: true,
        folderName: local.folderName,
        permissionLost: false,
      },
    })
  },

  async disconnectLocalFolder() {
    await _getLocalConnector().disconnect()
    set({ localFolder: { configured: false, folderName: null, permissionLost: false } })
  },

  async connectGoogleDrive() {
    const google = _getGoogleConnector()
    await google.connect()
    set((s) => ({
      googleDrive: {
        ...s.googleDrive,
        configured: true,
        accountEmail: google.accountEmail,
      },
    }))
  },

  async disconnectGoogleDrive() {
    await _getGoogleConnector().disconnect()
    set((s) => ({ googleDrive: { ...s.googleDrive, configured: false, accountEmail: null } }))
  },

  async saveGoogleClientId(clientId: string) {
    const trimmed = clientId.trim()
    await useKeysStore.getState().addKey('google-drive-client', 'Google OAuth Client ID', trimmed)
    _driveClientId = trimmed
    set((s) => ({ googleDrive: { ...s.googleDrive, googleClientId: trimmed || null } }))
  },

  async removeGoogleClientId() {
    await useKeysStore.getState().removeKey('google-drive-client')
    _driveClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''
    // Disconnect Drive — stored OAuth token is bound to the client ID that issued it
    if (_googleConnectorInstance?.isConfigured()) {
      await _googleConnectorInstance.disconnect()
    }
    set((_s) => ({
      googleDrive: {
        configured: false,
        accountEmail: null,
        googleClientId: _driveClientId || null,
      },
    }))
  },

  async saveToAll(imageUrl: string, model: string, filename?: string) {
    const local = _getLocalConnector()
    const google = _getGoogleConnector()

    // Skip entirely when nothing is configured — avoids pointless fetch in non-browser envs
    if (!local.isConfigured() && !google.isConfigured()) return

    const effectiveFilename = filename ?? galleryFilename(model)

    let blob: Blob
    try {
      const res = await fetch(imageUrl)
      blob = await res.blob()
    } catch {
      return
    }

    const saves: Promise<void>[] = []

    if (local.isConfigured()) {
      saves.push(local.save(blob, effectiveFilename).catch(() => undefined))
    }

    if (google.isConfigured()) {
      saves.push(google.save(blob, effectiveFilename).catch(() => undefined))
    }

    await Promise.all(saves)
  },
}))
